/**
 * Avion Rewards earn rate lookup by credit card BIN.
 *
 * Source: Epsilon redemption rates spreadsheet (RBC-2222).
 * BINs are stored without spaces. Lookup tries the full BIN string first,
 * then a 6-char slice, then a 4-char prefix — most-specific wins.
 *
 * Cards with no BIN (banking accounts) or unknown BINs should not render
 * the AvionPointsSummary component — callers treat `undefined` as ineligible.
 *
 * In staging/development, a separate test table maps Adyen test card BINs to
 * the same earn rate buckets so manual QA can exercise every scenario without
 * needing real Avion-issued cards.
 */

/**
 * Production — real Avion card BINs (Epsilon data, RBC-2222).
 * Points earned per CAD dollar spent, keyed by BIN (exact or 4-char prefix).
 */
export const AVION_BIN_EARN_RATES: Record<string, number> = {
  // ── Avion 1.25x ──────────────────────────────────────────────────────────
  "451401": 1.25, // IAV  RBC Avion Visa Infinite
  "464691": 1.25, // AVP  RBC Avion Visa Infinite Privilege / PPA
  "451409": 1.25, // GCP  RBC Avion Visa Platinum
  "464692": 1.25, // APP  RBC Avion Visa Infinite Privilege for Private Banking
  "451607": 1.25, // AIB / CPX / XPC / VAI  Business & Emerald Avion cards
  "486594": 1.25, // CAV  RBC Commercial Avion Visa

  // ── ION / ION+ 1.0x ──────────────────────────────────────────────────────
  "451014": 1.0, // ION / IOP  RBC ION Visa / ION+ Visa

  // ── US Dollar Visa Gold 1.0x ─────────────────────────────────────────────
  "451403": 1.0, // GUS  RBC US Dollar Visa Gold
  "451505": 1.0, // GUS  RBC US Dollar Visa Gold
  "451603": 1.0, // GUS  RBC US Dollar Visa Gold

  // ── Small Business 1.0x ──────────────────────────────────────────────────
  "451405": 1.0, // CCL  RBC Visa CreditLine for Small Business

  // ── Gold 0.5x (must be listed before 4512 prefix) ────────────────────────
  "451223": 0.5, // RVG  RBC Rewards Visa Gold

  // ── 4-digit prefix fallbacks (matched last) ───────────────────────────────
  "4512": 1.0, // GPR / PLP  RBC Rewards Visa Preferred
  "4510": 1.0, // CL2 / C2S / RVC  Signature, Student, Rewards+
};

/**
 * Staging / development — Adyen test card BINs mapped to equivalent buckets.
 * These cards work in both Adyen and Spreedly test environments.
 *
 * | Card number           | BIN    | Simulates     | Earn  |
 * |-----------------------|--------|---------------|-------|
 * | 4111 1111 1111 1111   | 411111 | Avion Infinite| 1.25x |
 * | 4646 4646 4646 4644   | 464646 | Classic / ION | 1.0x  |
 * | 4988 4388 4388 4305   | 498843 | Rewards Gold  | 0.5x  |
 * | 4444 3333 2222 1111   | 444433 | Banking acct  | —     | (not in table → section hidden)
 */
export const STAGING_BIN_EARN_RATES: Record<string, number> = {
  "411111": 1.25, // Adyen: 4111 1111 1111 1111 — simulates Avion Infinite (1.25x)
  "464646": 1.0, //  Adyen: 4646 4646 4646 4644 — simulates Classic / ION (1.0x)
  "498843": 0.5, //  Adyen: 4988 4388 4388 4305 — simulates Rewards Gold (0.5x)
  //              //  Adyen: 4444 3333 2222 1111 — not listed → section hidden
};

// Active table — compile-time selection.
// Non-production envs (local, development, staging) use Adyen test BINs so the
// section can be exercised without real Avion cards; production uses real BINs.
const IS_TEST_BIN_ENV =
  import.meta.env.VITE_ENV === "local" ||
  import.meta.env.VITE_ENV === "development" ||
  import.meta.env.VITE_ENV === "staging";

/**
 * Lookup earn rate for a given card BIN string.
 *
 * Tries (in order): full string → 6-char slice → 4-char prefix.
 * Returns `undefined` if no match (card not eligible for earn display).
 * Uses Adyen test BINs in non-production envs, real Avion BINs in production.
 */
export function getEarnRateByBin(bin: string | undefined): number | undefined {
  if (!bin) return undefined;

  const table = IS_TEST_BIN_ENV ? STAGING_BIN_EARN_RATES : AVION_BIN_EARN_RATES;

  return table[bin] ?? table[bin.slice(0, 6)] ?? table[bin.slice(0, 4)];
}
